<template>
  <div class="chatbot-page">
    <div class="chatbot-container">
      <div class="chatbot-header">
        <b-avatar
          variant="primary"
          size="36"
        >
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="2"
              r="1.5"
              fill="white"
            />
            <line
              x1="12"
              y1="3.5"
              x2="12"
              y2="6"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <rect
              x="3"
              y="6"
              width="18"
              height="14"
              rx="3"
              fill="white"
            />
            <rect
              x="6.5"
              y="10"
              width="3.5"
              height="3.5"
              rx="1"
              fill="#7367f0"
            />
            <rect
              x="14"
              y="10"
              width="3.5"
              height="3.5"
              rx="1"
              fill="#7367f0"
            />
            <rect
              x="7"
              y="15.5"
              width="10"
              height="2"
              rx="1"
              fill="#7367f0"
            />
          </svg>
        </b-avatar>
        <div class="chatbot-header-info">
          <h5 class="mb-0">
            Chatbot
          </h5>
          <small
            class="text-muted"
            :class="{
              'text-success': connectionStatus === 'online',
              'text-warning': connectionStatus === 'connecting',
              'text-danger': connectionStatus === 'offline',
            }"
          >
            {{ statusLabel }}
          </small>
        </div>
        <div class="chatbot-header-ai-toggle ml-auto">
          <!-- PROP-382: включение правой панели. Кнопка стоит в шапке рядом с режимами, а не
               внутри самой панели: орган управления, который виден только когда панель уже
               открыта, нечем открыть — это тот же невидимый переключатель, что снимали в
               PROP-365 (`/шаги`). -->
          <b-button
            :variant="sidebarMode === 'off' ? 'outline-secondary' : 'primary'"
            size="sm"
            class="chatbot-sidebar-toggle mr-1"
            :title="sidebarMode === 'off' ? 'Показать панель' : 'Скрыть панель'"
            @click="toggleSidebar"
          >
            <feather-icon
              icon="SidebarIcon"
              size="14"
            />
          </b-button>
          <b-form-select
            v-model="chat_mode"
            :options="chatModeOptions"
            size="sm"
            class="chatbot-mode-select mr-1"
          />
          <b-form-select
            v-model="aiMode"
            :options="aiModeOptions"
            size="sm"
            class="chatbot-ai-select mr-1"
            title="Кому разрешено отвечать"
            @change="aiModeChosen = true"
          />
          <!-- PROP-419: какой агент отвечает.
               PROP-424: «Локальный Клод» разблокирован — выбрать можно, но отвечает пока
               демонстрационный, и на выборе мы об этом говорим. -->
          <b-form-select
            v-model="agentKind"
            :options="agentKindOptions"
            size="sm"
            class="chatbot-agent-select"
            title="Какой агент отвечает"
            @change="onAgentKindChange"
          />
          <!-- PROP-468: режим работы агента. Показывается ТОЛЬКО когда домен раскатан
               (`agentmode` пришёл в whoami) — иначе переключатель ничего бы не менял.
               В заголовке сказано, что настройка общая для всех ИИ этого человека. -->
          <b-form-select
            v-if="agentMode"
            v-model="agentMode"
            :options="agentModeOptions"
            size="sm"
            class="chatbot-agentmode-select ml-1"
            :title="agentModeTitle"
            @change="onAgentModeChange"
          />
        </div>
      </div>

      <div
        v-if="participants.length"
        class="chatbot-participants"
      >
        <div
          v-for="p in participants"
          :key="p.id"
          class="chatbot-participant"
          :class="{ 'chatbot-participant--off': !p.inChain, 'chatbot-participant--next': p.next }"
          :title="p.role + (p.detail ? ' — ' + p.detail : '')"
        >
          <b-avatar
            v-if="p.id === 'user'"
            size="20"
            class="chatbot-participant-avatar"
            :text="userInitials"
            :style="{ backgroundColor: avatarOf(p.id).color }"
          />
          <b-avatar
            v-else
            size="20"
            class="chatbot-participant-avatar"
            :style="{ backgroundColor: avatarOf(p.id).color }"
          >
            <feather-icon
              :icon="avatarOf(p.id).icon"
              size="12"
            />
          </b-avatar>
          <span
            class="chatbot-light"
            :class="'chatbot-light--' + p.status"
          />
          <span class="chatbot-participant-name">{{ p.name }}</span>
        </div>
      </div>

      <div
        ref="chatLog"
        class="chatbot-messages"
        @click="onChatClick"
      >
        <div
          v-for="(msg, index) in visibleMessages"
          :key="index"
          class="chatbot-message-row"
          :class="{
            'chatbot-message-row--user': msg.sender === 'user',
            'chatbot-message-row--service': isService(msg),
          }"
        >
          <!-- Служебная строка отладочного потока: не разговор, а показания приборов.
               Поэтому без пузыря, аватара и подписи — иначе она спорит за внимание с
               настоящими репликами и превращает переписку в простыню.
               PROP-366: время стоит В САМОЙ строке, а не только в подсказке. Показания
               приборов читают по времени — «сколько он на этом сидит», — а подсказка требует
               навести курсор на каждую строку по очереди.
               PROP-367: время — В КОНЦЕ строки. Слева читается занятие («что он делает
               сейчас»), и колонка времени перед ним отодвигала главное на полтора сантиметра
               вправо, разное на каждой строке. -->
          <div
            v-if="isService(msg)"
            class="chatbot-service"
            :title="formatTime(msg.at)"
          >
            <span
              class="chatbot-service-text"
              v-html="renderMarkdown(msg.text)"
            />
            <span class="chatbot-service-time">{{ formatTime(msg.at) }}</span>
          </div>
          <b-avatar
            v-else-if="msg.sender === 'bot'"
            size="32"
            class="chatbot-avatar"
            :style="{ backgroundColor: avatarOf(roleOfMessage(msg)).color }"
            :title="msg.author"
          >
            <feather-icon
              :icon="avatarOf(roleOfMessage(msg)).icon"
              size="18"
            />
          </b-avatar>
          <div
            v-if="!isService(msg)"
            class="chatbot-bubble"
            :class="msg.sender === 'user' ? 'chatbot-bubble--user' : 'chatbot-bubble--bot'"
          >
            <div class="chatbot-meta">
              <span class="chatbot-author">{{ msg.author }}</span>
              <span class="chatbot-time">{{ formatTime(msg.at) }}</span>
            </div>
            <div
              v-if="msg.format === 'markdown'"
              class="chatbot-markdown"
              v-html="renderMarkdown(msg.text)"
            />
            <template v-else>
              {{ msg.text }}
            </template>
            <button
              class="chatbot-copy-btn"
              :title="msg.copied ? 'Скопировано!' : 'Копировать'"
              @click="copyMessage(index)"
            >
              <feather-icon
                :icon="msg.copied ? 'CheckIcon' : 'CopyIcon'"
                size="14"
              />
            </button>
          </div>
          <b-avatar
            v-if="msg.sender === 'user' && !isService(msg)"
            size="32"
            :text="userInitials"
            class="chatbot-avatar"
            :style="{ backgroundColor: avatarOf('user').color }"
            :title="msg.author"
          />
        </div>

        <div
          v-if="isWaiting"
          class="chatbot-message-row"
        >
          <b-avatar
            size="32"
            class="chatbot-avatar"
            :style="{ backgroundColor: avatarOf(waitingRole).color }"
            :title="waitingName"
          >
            <feather-icon
              :icon="avatarOf(waitingRole).icon"
              size="18"
            />
          </b-avatar>
          <div class="chatbot-bubble chatbot-bubble--bot chatbot-typing">
            <span class="chatbot-typing-label">{{ waitingName }} думает</span>
            <span class="chatbot-typing-dot" />
            <span class="chatbot-typing-dot" />
            <span class="chatbot-typing-dot" />
            <!-- PROP-408: сколько уже ждём. Ответ агента идёт минутами, и без счётчика «думает»
                 неотличимо от зависшего. -->
            <span class="chatbot-typing-elapsed">{{ waitingLabel }}</span>
          </div>
        </div>
      </div>

      <div
        v-if="contextObject || contextEntity"
        class="chatbot-statusbar"
      >
        <feather-icon
          icon="BoxIcon"
          size="14"
          class="chatbot-statusbar-icon"
        />
        <span class="chatbot-statusbar-label">Контекст:</span>
        <template v-if="contextObject">
          <router-link
            :to="`/cubus/object/${contextObject.objectId}`"
            class="chatbot-statusbar-link"
          >
            {{ contextObject.objectName }}
          </router-link>
          <span class="chatbot-statusbar-id">#{{ contextObject.objectId }}</span>
        </template>
        <template v-if="contextEntity">
          <feather-icon
            icon="ChevronRightIcon"
            size="14"
            class="chatbot-statusbar-separator"
          />
          <span class="chatbot-statusbar-entity">{{ contextEntity.entityName }}</span>
          <span class="chatbot-statusbar-id">#{{ contextEntity.entityId }}</span>
        </template>
      </div>

      <div class="chatbot-input-area">
        <!-- PROP-384: команды платформы предлагаются сразу после «/». Раньше их надо было
             ЗНАТЬ: список был только в ответе на `/help`, то есть в команде, о которой тоже
             надо было знать. Подсказка снимает это ровно там, где человек начинает набирать. -->
        <div
          v-if="commandHints.length"
          class="chatbot-commands"
        >
          <div
            v-for="(c, i) in commandHints"
            :key="c.cmd"
            class="chatbot-command"
            :class="{ 'chatbot-command--active': i === commandIndex }"
            @mousedown.prevent="pickCommand(c)"
            @mouseenter="commandIndex = i"
          >
            <span class="chatbot-command-name">{{ c.cmd }}<template v-if="c.args"> {{ c.args }}</template></span>
            <span class="chatbot-command-hint">{{ c.hint }}</span>
          </div>
        </div>
        <b-form
          class="chatbot-form"
          @submit.prevent="onSubmit"
        >
          <!-- PROP-418: поле резиновое — растёт под текст до восьми строк, дальше
               прокручивается. Однострочный ввод прятал длинный запрос: видно было только
               хвост. Enter отправляет (как и раньше), Shift+Enter — перенос строки. -->
          <b-form-textarea
            ref="chatInput"
            v-model="inputText"
            rows="1"
            max-rows="8"
            no-resize
            class="chatbot-textarea"
            placeholder="Введите сообщение или «/» для команд..."
            autocomplete="off"
            @keydown.enter.exact="onEnter"
            @keydown.up="onArrowUp"
            @keydown.down="onArrowDown"
            @keydown.tab="onTab"
            @keydown.esc="commandsDismissed = true"
          />
          <b-button
            v-if="isSpeechSupported"
            :variant="isRecording ? 'danger' : 'outline-secondary'"
            :class="{ 'chatbot-mic-recording': isRecording }"
            @click="toggleVoiceInput"
          >
            <feather-icon
              icon="MicIcon"
              size="16"
            />
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :disabled="!inputText.trim()"
          >
            <feather-icon
              icon="SendIcon"
              size="16"
            />
          </b-button>
        </b-form>
      </div>
    </div>

    <!-- PROP-382: правая панель. Три состояния, и разница между двумя открытыми не
         косметическая: «поверх» не отнимает у переписки ни пикселя и годится, чтобы
         заглянуть и закрыть; «рядом» сужает переписку и годится, чтобы держать открытым
         постоянно. Выбор за человеком — оба сценария законны, а угадать за него нельзя. -->
    <aside
      v-if="sidebarMode !== 'off'"
      class="chatbot-sidebar"
      :class="'chatbot-sidebar--' + sidebarMode"
      :style="{ width: sidebarWidth + 'px' }"
    >
      <!-- Ползунок ширины. Тянется левый край, поэтому движение влево ШИРИТ панель. -->
      <div
        class="chatbot-sidebar-resizer"
        :class="{ 'chatbot-sidebar-resizer--active': resizing }"
        title="Потяните, чтобы изменить ширину"
        @mousedown.prevent="startResize"
        @dblclick="sidebarWidth = SIDEBAR_DEFAULT_WIDTH"
      />
      <div class="chatbot-sidebar-header">
        <span class="chatbot-sidebar-title">Ход работы агента</span>
        <div class="chatbot-sidebar-actions">
          <b-button
            size="sm"
            :variant="sidebarMode === 'overlay' ? 'primary' : 'outline-secondary'"
            class="chatbot-sidebar-mode"
            title="Поверх переписки"
            @click="setSidebarMode('overlay')"
          >
            <feather-icon
              icon="LayersIcon"
              size="13"
            />
          </b-button>
          <b-button
            size="sm"
            :variant="sidebarMode === 'docked' ? 'primary' : 'outline-secondary'"
            class="chatbot-sidebar-mode"
            title="Рядом с перепиской"
            @click="setSidebarMode('docked')"
          >
            <feather-icon
              icon="ColumnsIcon"
              size="13"
            />
          </b-button>
          <b-button
            size="sm"
            variant="flat-secondary"
            class="chatbot-sidebar-mode"
            title="Закрыть панель"
            @click="setSidebarMode('off')"
          >
            <feather-icon
              icon="XIcon"
              size="13"
            />
          </b-button>
        </div>
      </div>

      <div class="chatbot-sidebar-body">
        <!-- Вехи хода работы в переписке ЖИВУТ МГНОВЕНИЕ: они не сохраняются в историю
             (PROP-323) и после ответа проследить, чем агент был занят, уже нечем. Здесь они
             копятся за сеанс. Из ленты чата ничего не убрано: панель добавляет журнал, а не
             переносит показ, — иначе закрытая панель означала бы потерю того, что владелец
             принимал в PROP-365/366. -->
        <div
          v-if="!agentLog.length"
          class="chatbot-sidebar-empty"
        >
          Здесь появится то, чем агент занят: вызовы инструментов, команды, файлы.
          Пока он ничего не делал в этом сеансе.
        </div>
        <div
          v-for="(step, i) in agentLog"
          :key="i"
          class="chatbot-sidebar-step"
        >
          <span class="chatbot-sidebar-step-time">{{ formatTime(step.at) }}</span>
          <span class="chatbot-sidebar-step-text">{{ step.text }}</span>
        </div>
      </div>

      <div class="chatbot-sidebar-footer">
        <span
          v-for="p in participants"
          :key="p.id"
          class="chatbot-sidebar-participant"
          :title="p.role + (p.detail ? ' — ' + p.detail : '')"
        >
          <span
            class="chatbot-light"
            :class="'chatbot-light--' + p.status"
          />
          {{ p.name }}
        </span>
      </div>
    </aside>
  </div>
</template>

<script>
import {
  BAvatar, BForm, BFormTextarea, BButton, BFormSelect,
} from 'bootstrap-vue'
import {
  renderChatMarkdown, handleInternalClick, internalLinksOf, saysDoNotOpen,
} from '@/cubus/services/chatMarkdown'
import useCubus from '@/cubus/services/useCubus'
import { getConfig } from '@/libs/axios'
import {
  parseCommand, matchCommands, HELP_TEXT, atFirstLine, atLastLine,
} from '@/cubus/services/chatCommands'
import {
  MODE_OPTIONS as AGENT_MODE_OPTIONS, modeHint, modeLabel, loadAgentMode, saveAgentMode,
} from '@/cubus/services/agentMode'
import { AI_MODE_OPTIONS } from '@/cubus/services/aiModes'

const MAX_RECONNECT_ATTEMPTS = 10
const RECONNECT_INTERVAL = 3000

// PROP-382: правая панель. Ширина и режим — настройка одного человека на одном браузере,
// поэтому живут в localStorage, а не на сервере: делить их между устройствами не за чем, а
// чтение обёрнуто в try/catch — в приватном окне и при запрещённых куках обращение к хранилищу
// БРОСАЕТ, и незащищённое чтение уронило бы весь чат ради ширины панели.
const SIDEBAR_KEY = 'cubus.chatbot.sidebar'
const SIDEBAR_MODES = ['off', 'overlay', 'docked']
const SIDEBAR_DEFAULT_WIDTH = 340
const SIDEBAR_MIN_WIDTH = 240
// Потолок считается от окна, а не константой: на узком экране панель «рядом» иначе съедает
// переписку целиком, ради которой её и открыли.
const sidebarMaxWidth = () => Math.max(SIDEBAR_MIN_WIDTH, Math.min(720, window.innerWidth - 420))
// Журнал вех не должен расти бесконечно: агент делает шаги пачками, а панель живёт всю сессию.
const SIDEBAR_LOG_LIMIT = 500
// Кем подписаны сообщения самой платформы: разбор команды делает Cubus, а не модель.
const AUTHOR_CUBUS = 'Cubus'
const AUTHOR_AGENT = 'Агент'

// Аватары участников (PROP-330). Ключи — те же роли, что сервер шлёт в списке участников
// (`ChatParticipants`: user, cubus, assistant, agent), поэтому строка в шапке и пузырь в ленте
// показывают ОДНО лицо: карта одна на оба места, разъехаться им не на чем.
// Цвета намеренно взяты ВНЕ палитры светофора (зелёный/жёлтый/красный): аватар отвечает на
// вопрос «кто это», лампа рядом — «в каком он состоянии», и общий цвет у двух разных смыслов
// читался бы как «у ассистента всё хорошо» ровно там, где лампа красная.
// Аватар один на роль (решение владельца 05.09): у ассистента одно лицо независимо от
// провайдера, имя модели остаётся в подписи (PROP-324).
const AVATARS = {
  user: { icon: 'UserIcon', color: '#00cfe8' },
  cubus: { icon: 'BoxIcon', color: '#7367f0' },
  assistant: { icon: 'ZapIcon', color: '#0d9488' },
  agent: { icon: 'TerminalIcon', color: '#475569' },
  unknown: { icon: 'HelpCircleIcon', color: '#b9b9c3' },
}

// Подпись сообщения → роль. Платформа и агент подписываются постоянными строками, а ассистент —
// ИМЕНЕМ МОДЕЛИ (`claude-opus-4-6`, `gemma-3-27b`, …), которое зависит от домена и провайдера:
// перечислить его нечем, поэтому опознаются двое, а всё остальное подписанное — ответ
// ассистента. Сообщение БЕЗ подписи получает нейтральное лицо, а не чужое: пустой автор
// приходит из старой вкладки и приписывать его Кубусу значило бы соврать картинкой.
const AUTHOR_ROLES = { [AUTHOR_CUBUS]: 'cubus', [AUTHOR_AGENT]: 'agent' }

export default {
  name: 'CbsChatbot',
  components: {
    BAvatar,
    BForm,
    BFormTextarea,
    BButton,
    BFormSelect,
  },
  data() {
    return {
      SIDEBAR_DEFAULT_WIDTH,
      // 'off' | 'overlay' (поверх переписки) | 'docked' (рядом с ней)
      sidebarMode: 'off',
      sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
      // Куда возвращаться по кнопке в шапке: человек, закрывший панель в режиме «рядом»,
      // ждёт «рядом» и при следующем открытии.
      sidebarLastOpen: 'docked',
      resizing: false,
      // Вехи хода работы за сеанс: в переписке они не сохраняются и исчезают вместе с ответом.
      agentLog: [],
      inputText: '',
      // PROP-401: по умолчанию ВЗВЕДЕНО для каждого ушедшего агенту запроса; снимается только
      // явным запретом в запросе («не открывай»). На первом ответе по существу: ровно одна
      // внутренняя ссылка — открываем её в основной области.
      autoOpenArmed: false,
      // Подсказка команд (PROP-384): выбранная строка и признак «человек её закрыл».
      commandIndex: 0,
      commandsDismissed: false,
      messages: [
        {
          sender: 'bot', text: 'Здравствуйте! Чем могу помочь?', author: AUTHOR_CUBUS, at: new Date(),
        },
      ],
      connectionStatus: 'connecting',
      ws: null,
      reconnectAttempts: 0,
      reconnectTimer: null,
      isRecording: false,
      recognition: null,
      wasVoiceInput: false,
      commandHistory: [],
      historyIndex: -1,
      // PROP-426: листаем ли историю прямо сейчас. Пока не листаем, стрелки — про курсор.
      historyBrowsing: false,
      fromHistory: false,
      contextObject: null,
      contextEntity: null,
      participants: [],
      participantsTimer: null,
      // Кому разрешено отвечать (PROP-329, PROP-363): «Без ИИ» — только Кубус, «Ассистент» —
      // Кубус и встроенный ИИ, «Каскад» — цепочка целиком (агент подхватывает ПОСЛЕ отказа
      // ассистента), «Агент» — Кубус и сразу агент, без шага ассистента.
      // PROP-381: умолчание — «Агент». Режим нигде не сохраняется и сбрасывается при каждом
      // открытии чата, поэтому умолчание — это и есть то, чем пользуются: выбор в шапке живёт
      // до закрытия вкладки.
      // ⚠️ Умолчание подстраховано: там, где агента на инстансе нет (боевой kbtu — именно
      // такой), режим «Агент» отвечал бы только на распознанные команды, а на всё прочее —
      // общим «не понял», НЕ говоря, что агента тут нет. Молчаливая деградация — тот же класс,
      // что PROP-365 и BUG-368, поэтому при недоступном агенте умолчание опускается до
      // «Каскада»; см. adjustDefaultMode.
      // ⚠️ «Каскад» до PROP-363 назывался «Агент»: значение (`ai_agent`) прежнее, переименована
      // только подпись — иначе два разных режима звались бы одинаково.
      aiMode: 'agent',
      // Трогал ли режим человек. Нужно ровно для одного: подстраховка ниже правит УМОЛЧАНИЕ и
      // не имеет права переспорить сделанный выбор.
      aiModeChosen: false,
      // PROP-468: режим работы агента. `null` значит «в этом домене режима нет» (домен раскатан
      // до PROP-464) — тогда переключателя не показываем вовсе: он ничего бы не менял.
      agentMode: null,
      agentModeOptions: AGENT_MODE_OPTIONS,
      aiModeOptions: AI_MODE_OPTIONS,
      isWaiting: false,
      // Кого ждём прямо сейчас: пусто — считаем по цепочке, иначе id участника с сервера.
      waitingFor: '',
      waitingTimer: null,
      // PROP-408: секундомер ожидания. `isWaiting` выставляется из нескольких мест, поэтому
      // отсчёт заводится наблюдателем за ПЕРЕХОДОМ в ожидание — так ни одна точка не забыта.
      waitingSince: 0,
      waitingElapsed: 0,
      waitingTicker: null,
      chat_mode: 'chat',
      chatModeOptions: [
        { value: 'chat', text: 'Chat' },
        { value: 'debug', text: 'Debug' },
      ],
      // PROP-419: чей агент отвечает.
      // PROP-424: второй вариант разблокирован по указанию владельца. Работающим он не стал —
      // проводки к локальному Клоду ещё нет, отвечает демонстрационный; о чём и говорим прямо
      // при выборе, чтобы переключатель не врал молчанием.
      agentKind: 'demo',
      agentKindOptions: [
        { value: 'demo', text: 'Демонстрационный' },
        { value: 'local', text: 'Локальный Клод (в разработке)' },
      ],
    }
  },
  computed: {
    /** PROP-468: подпись текущего режима работы агента. */
    agentModeLabel() {
      return modeLabel(this.agentMode)
    },
    /**
     * Заголовок переключателя. ⚠️ Про «все ИИ в этом домене» здесь сказано намеренно: режим —
     * свойство ПОЛЬЗОВАТЕЛЯ, а не вкладки, и человек должен узнать об этом до переключения,
     * а не по последствиям в другом инструменте.
     */
    agentModeTitle() {
      return `Режим работы агента. ${modeHint(this.agentMode)} `
        + 'Настройка общая для всех ИИ, работающих в этом домене под вашей учётной записью.'
    },
    /** Что предложить под начатый ввод. Пусто — подсказки нет (не команда либо её закрыли). */
    commandHints() {
      return this.commandsDismissed ? [] : matchCommands(this.inputText)
    },
    // PROP-408: длительность ожидания секундомером `м:сс`.
    waitingLabel() {
      const s = Math.max(0, this.waitingElapsed)
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
    },
    // PROP-397: «Чат» показывает только значимые сообщения — реплики собеседников. Служебные
    // строки (показания Debug, вехи хода работы, передача управления) видны лишь в «Отладке».
    // Скрываем целиком, а не приглушаем: в обычном разговоре они лишний шум.
    visibleMessages() {
      if (this.chat_mode === 'debug') return this.messages
      return this.messages.filter(m => !this.isService(m))
    },
    userInitials() {
      const session = useCubus.getSession()
      if (session && session.user && session.user.name) {
        return session.user.name.substring(0, 2).toUpperCase()
      }
      return 'ME'
    },
    userName() {
      const session = useCubus.getSession()
      if (session && session.user && session.user.name) return session.user.name
      return 'Вы'
    },
    statusLabel() {
      const labels = {
        online: 'Online',
        connecting: 'Connecting...',
        offline: 'Offline',
      }
      return labels[this.connectionStatus] || 'Offline'
    },
    isSpeechSupported() {
      return !!(window.SpeechRecognition || window.webkitSpeechRecognition)
    },
    // Кого ждём, пока крутятся точки: сервер уже считает следующий шаг цепочки и присылает его
    // в списке участников (`next`), поэтому аватар ожидания — это лицо того, кто и ответит, а
    // не общий робот. Списка ещё нет (первое подключение) — ждём Кубуса: без ИИ отвечает он.
    waitingParticipant() {
      // `waitingFor` — это ФАКТ («вопрос уже уехал агенту»), а `next` — расчёт по режиму.
      // Факт старше: после передачи агенту список участников по-прежнему считает следующим
      // шагом Ассистента, и без этого над точками висело бы чужое лицо.
      if (this.waitingFor) {
        return this.participants.find(p => p.id === this.waitingFor) || { id: this.waitingFor, name: 'Агент' }
      }
      return this.participants.find(p => p.next) || null
    },
    waitingRole() {
      return this.waitingParticipant ? this.waitingParticipant.id : 'cubus'
    },
    waitingName() {
      return this.waitingParticipant ? this.waitingParticipant.name : 'Cubus'
    },
  },
  watch: {
    // PROP-408: секундомер заводится на переходе в ожидание и гаснет с ним. Служебные строки
    // держат `isWaiting` истинным, не меняя его, — значит счётчик не сбрасывается на вехах.
    isWaiting(now) {
      clearInterval(this.waitingTicker)
      this.waitingTicker = null
      if (!now) return
      this.waitingSince = Date.now()
      this.waitingElapsed = 0
      this.waitingTicker = setInterval(() => {
        this.waitingElapsed = Math.floor((Date.now() - this.waitingSince) / 1000)
      }, 1000)
    },
    inputText() {
      // Новый ввод — новая подсказка: закрытие Esc относится к тому, что было набрано, а не
      // к полю навсегда.
      this.commandsDismissed = false
      this.commandIndex = 0
      // PROP-426: человек начал править — листание истории закончилось.
      if (!this.fromHistory) {
        this.historyBrowsing = false
        this.historyIndex = -1
      }
    },
    // Режим меняет состав цепочки, поэтому светофор пересчитывается сразу, а не после
    // следующего сообщения: иначе панель полминуты показывает снятый человеком шаг.
    aiMode() {
      this.requestParticipants()
    },

  },
  created() {
    useCubus.setPageTitle('Chatbot')
  },
  mounted() {
    this.restoreSidebar()
    // Ширина панели зависит от окна: потолок считается от него, и без пересчёта панель
    // «рядом» на суженном окне съедает переписку целиком.
    window.addEventListener('resize', this.applyNarrowScreen)
    this.connectWebSocket()
    this.focusInput()
    // Участник может отвалиться и без переписки — раннер останавливают, провайдер перестаёт
    // отвечать. Минута выбрана под TTL серверных проб: чаще спрашивать нечего, ответ тот же.
    this.participantsTimer = setInterval(this.requestParticipants, 60000)
    this.loadAgentMode()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.applyNarrowScreen)
    if (this.participantsTimer) {
      clearInterval(this.participantsTimer)
      this.participantsTimer = null
    }
    clearTimeout(this.waitingTimer)
    clearInterval(this.waitingTicker)
    this.closeWebSocket()
    if (this.recognition) {
      this.recognition.abort()
      this.recognition = null
    }
  },
  methods: {
    /** Лицо роли; неизвестная роль получает нейтральное, а не падает вёрсткой. */
    avatarOf(role) {
      return AVATARS[role] || AVATARS.unknown
    },
    /** Роль автора сообщения — по подписи (PROP-324), а не по порядку в цепочке: порядок
     *  говорит, кто отвечает СЕЙЧАС, а в ленте лежат ответы разных источников вперемешку. */
    roleOfMessage(msg) {
      if (msg.sender === 'user') return 'user'
      const author = (msg.author || '').trim()
      if (!author) return 'unknown'
      return AUTHOR_ROLES[author] || 'assistant'
    },
    getWsUrl() {
      const baseURL = getConfig()
      const url = new URL('/ws/chat', baseURL)
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
      return url.toString()
    },
    getToken() {
      // PROP-086: токен из Vuex-сессии (в памяти), а не из localStorage. Нужен явно, потому
      // что AI-сервис живёт на другом origin, куда httpOnly-cookie не приложится.
      const s = this.$store.getters['cubus-store/SESSION']
      return (s && s.token) || ''
    },
    connectWebSocket() {
      this.closeWebSocket()
      this.connectionStatus = 'connecting'

      const url = this.getWsUrl()
      this.ws = new WebSocket(url)

      this.ws.onopen = () => {
        this.connectionStatus = 'online'
        this.reconnectAttempts = 0
        this.ws.send(JSON.stringify({
          type: 'auth',
          token: this.getToken(),
        }))
      }

      this.ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'participants') {
            this.participants = data.items || []
            this.adjustDefaultMode()
            return
          }
          if (data.type === 'connected') {
            this.requestParticipants()
            return
          }
          if (data.type === 'message') {
            // PROP-355: разговор заканчивают не все сообщения. Служебная строка Debug и
            // «передал агенту» приходят ПЕРЕД ответом, и раньше любое из них гасило «думает»:
            // человек оставался ждать настоящий ответ в тишине, глядя на законченную с виду
            // переписку. Теперь ждём, пока не придёт сообщение, которое отвечает по существу.
            const pending = data.kind === 'service' || data.kind === 'pending'
            this.isWaiting = pending
            if (data.waitingFor) this.waitingFor = data.waitingFor
            if (!pending) { this.waitingFor = ''; clearTimeout(this.waitingTimer) }
            this.pushMessage({
              sender: 'bot',
              text: data.text,
              format: data.format,
              author: data.fromName,
              at: data.ts,
              kind: data.kind,
              // Веха хода работы: в историю не ложится и рисуется служебной строкой (PROP-366).
              transient: data.transient === true,
            })
            // PROP-382: и одновременно — в журнал правой панели. Копия, а не перенос: в ленте
            // веха живёт мгновение и тает вместе с ответом, а в панели остаётся за сеанс.
            if (data.transient === true) this.rememberStep(data.text, data.ts)
            // PROP-400: ответ по существу на запрос «открой …». Если он предлагает РОВНО ОДНУ
            // внутреннюю ссылку — открываем её сами. Взвод одноразовый: снимаем на первом же
            // ответе по существу, сколько бы ссылок в нём ни было (ноль или несколько вариантов —
            // выбор за человеком), иначе следующий ответ увёл бы не туда.
            if (this.autoOpenArmed && !pending && data.transient !== true) {
              this.autoOpenArmed = false
              // PROP-423: агент называет объект отдельным блоком `link`. Разбор текста остаётся
              // запасным путём: раннер выкатывается отдельно от фронта.
              if (data.link) this.openInternalHref(data.link)
              else {
                const links = internalLinksOf(data.text)
                if (links.length === 1) this.openInternalHref(links[0])
              }
            }
            if (data.context) {
              if (data.context.objectId) {
                this.contextObject = { objectId: data.context.objectId, objectName: data.context.objectName }
              } else {
                this.contextObject = null
              }
              if (data.context.entityId) {
                this.contextEntity = { entityId: data.context.entityId, entityName: data.context.entityName }
              } else {
                this.contextEntity = null
              }
            }
            this.scrollToBottom()
            this.requestParticipants()
          } else if (data.type === 'error') {
            this.isWaiting = false
            clearTimeout(this.waitingTimer)
            this.pushMessage({ sender: 'bot', text: `Ошибка: ${data.text}` })
            this.scrollToBottom()
          }
        } catch {
          // ignore non-JSON messages
        }
      }

      this.ws.onclose = () => {
        this.connectionStatus = 'offline'
        this.scheduleReconnect()
      }

      this.ws.onerror = () => {
        this.connectionStatus = 'offline'
      }
    },
    /**
     * Состояние участников (PROP-326). Режим ИИ живёт здесь, а не на сервере, поэтому уходит
     * вместе с запросом: от него зависит, кому вообще разрешено отвечать.
     */
    // --- правая панель (PROP-382) ---------------------------------------------
    /**
     * Восстановить режим и ширину панели.
     *
     * Обращение к localStorage целиком в try/catch: в приватном окне и при запрещённых куках
     * оно БРОСАЕТ, а не возвращает пусто, и незащищённое чтение уронило бы весь чат ради
     * настройки панели. Значения из хранилища недоверенные — их правит кто угодно через
     * консоль, — поэтому режим сверяется со списком, а ширина зажимается в пределы.
     */
    restoreSidebar() {
      let saved = null
      try {
        saved = JSON.parse(localStorage.getItem(SIDEBAR_KEY) || 'null')
      } catch (e) {
        saved = null
      }
      if (!saved || typeof saved !== 'object') return
      if (SIDEBAR_MODES.includes(saved.mode)) {
        this.sidebarMode = saved.mode
        if (saved.mode !== 'off') this.sidebarLastOpen = saved.mode
      }
      if (SIDEBAR_MODES.includes(saved.lastOpen) && saved.lastOpen !== 'off') {
        this.sidebarLastOpen = saved.lastOpen
      }
      this.sidebarWidth = this.clampSidebarWidth(Number(saved.width) || SIDEBAR_DEFAULT_WIDTH)
      this.applyNarrowScreen()
    },

    saveSidebar() {
      try {
        localStorage.setItem(SIDEBAR_KEY, JSON.stringify({
          mode: this.sidebarMode, lastOpen: this.sidebarLastOpen, width: this.sidebarWidth,
        }))
      } catch (e) { /* хранилище недоступно — панель просто не переживёт перезагрузку */ }
    },

    clampSidebarWidth(px) {
      return Math.min(sidebarMaxWidth(), Math.max(SIDEBAR_MIN_WIDTH, Math.round(px)))
    },

    setSidebarMode(mode) {
      if (!SIDEBAR_MODES.includes(mode)) return
      this.sidebarMode = mode
      if (mode !== 'off') this.sidebarLastOpen = mode
      this.sidebarWidth = this.clampSidebarWidth(this.sidebarWidth)
      this.saveSidebar()
    },

    /** Кнопка в шапке: закрыть или вернуть панель в том виде, в каком её закрыли. */
    toggleSidebar() {
      this.setSidebarMode(this.sidebarMode === 'off' ? this.sidebarLastOpen : 'off')
    },

    /**
     * Тянем ЛЕВЫЙ край: движение влево ширит панель, поэтому дельта берётся со знаком минус.
     *
     * Слушатели вешаются на документ, а не на сам ползунок: курсор во время перетаскивания
     * уходит с тонкой полоски за первые же миллиметры, и обработчик на элементе теряет
     * событие — тяга обрывается на середине.
     */
    startResize(event) {
      const startX = event.clientX
      const startWidth = this.sidebarWidth
      this.resizing = true
      const onMove = e => { this.sidebarWidth = this.clampSidebarWidth(startWidth - (e.clientX - startX)) }
      const onUp = () => {
        this.resizing = false
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        // Текст в переписке снова выделяется мышью: на время тяги выделение гасится, иначе
        // движение курсора выделяет всё подряд.
        document.body.style.userSelect = ''
        this.saveSidebar()
      }
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },

    /**
     * На узком экране режим «рядом» невозможен: переписка сжимается до нечитаемой колонки.
     * Панель не закрывается — она переводится в «поверх», где ширина экрана ей не мешает.
     */
    applyNarrowScreen() {
      if (this.sidebarMode === 'docked' && window.innerWidth < 1024) this.sidebarMode = 'overlay'
      this.sidebarWidth = this.clampSidebarWidth(this.sidebarWidth)
    },

    /** Веха хода работы — в журнал панели. В ленте чата она по-прежнему появляется и тает. */
    rememberStep(text, at) {
      this.agentLog.push({ text: String(text || '').replace(/^\s*⚙️\s*/, ''), at: at || new Date() })
      if (this.agentLog.length > SIDEBAR_LOG_LIMIT) {
        this.agentLog.splice(0, this.agentLog.length - SIDEBAR_LOG_LIMIT)
      }
    },

    /**
     * PROP-381: умолчание «Агент» не должно оставлять человека в режиме, который не отвечает.
     *
     * На инстансе, где агент не настроен (боевой kbtu — именно такой), режим «Агент» гасит
     * ассистента, и на всё, кроме распознанных команд, приходит общее «не понял» БЕЗ слова о
     * том, что агента здесь нет. Это молчаливая деградация — тот же класс, что PROP-365
     * (показ хода работы был выключен без признака) и BUG-368 (ответ терялся без ошибки).
     *
     * Поэтому первый же ответ сервера со списком участников решает: агент недоступен — умолчание
     * опускается до «Каскада», где отвечает хотя бы ассистент. Правится ТОЛЬКО умолчание: если
     * человек выбрал режим сам, его выбор не трогается, даже когда агент лежит — он имеет право
     * видеть погашенного участника и понимать, почему тишина.
     *
     * `health` — состояние участника БЕЗ учёта режима: сервер отдаёт его как раз для таких
     * решений, чтобы «серый, потому что выключен» не путался со «сломан».
     */
    adjustDefaultMode() {
      if (this.aiModeChosen || this.aiMode !== 'agent') return
      const agent = this.participants.find(p => p.id === 'agent')
      if (agent && agent.health === 'red') this.aiMode = 'ai_agent'
    },

    requestParticipants() {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
      this.ws.send(JSON.stringify({
        type: 'participants',
        aiMode: this.aiMode,
        // Для старого бэкенда, который про режимы ещё не знает.
        useAi: this.aiMode !== 'off',
        token: this.getToken(),
      }))
    },
    closeWebSocket() {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
      if (this.ws) {
        this.ws.onclose = null
        this.ws.onerror = null
        this.ws.close()
        this.ws = null
      }
    },
    scheduleReconnect() {
      if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return
      this.reconnectAttempts += 1
      this.reconnectTimer = setTimeout(() => {
        this.connectWebSocket()
      }, RECONNECT_INTERVAL)
    },
    toggleVoiceInput() {
      if (this.isRecording) {
        this.recognition.stop()
        return
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      this.recognition = new SpeechRecognition()
      this.recognition.lang = 'ru-RU'
      this.recognition.interimResults = false
      this.recognition.continuous = false

      this.recognition.onresult = event => {
        const { transcript } = event.results[0][0]
        this.inputText += (this.inputText ? ' ' : '') + transcript
        this.wasVoiceInput = true
        this.$nextTick(() => {
          this.focusInput()
        })
      }

      this.recognition.onend = () => {
        this.isRecording = false
        this.$nextTick(() => {
          this.focusInput()
        })
      }

      this.recognition.onerror = () => {
        this.isRecording = false
      }

      this.recognition.start()
      this.isRecording = true
    },
    capitalizeWords(str) {
      return str.replace(/\S+/g, word => word.charAt(0).toUpperCase() + word.slice(1))
    },
    formatCommand(text) {
      const match = text.match(/^(создай\s+объект\s+)(.+)$/i)
      if (match) {
        return match[1] + this.capitalizeWords(match[2])
      }
      return text
    },
    getHelpText() {
      return `${HELP_TEXT}

**Текстовые команды:**
- **привет** — приветствие
- **кто я** — информация о пользователе
- **где я** — текущий домен
- **статус** — статус сессии
- **открой ххх** — найти объект по имени
- **сущность ххх** — описание сущности по ID
- **покажи объект ххх** — информация об объекте по ID
- **выбери ххх** — выбрать объект из результатов поиска
- **создай ааа ббб** — создать объект (ааа = тип, ббб = имя)
- **добавь атрибут ааа** — добавить атрибут в текущий объект
- **добавь записи в ааа** — массовое добавление записей в объект
- **покажи атрибуты** — атрибуты текущего объекта
- **покажи параметры формы** — параметры формы текущего объекта
- **обнови параметры формы {...}** — обновить параметры формы
- **контекст** — показать текущий объект
- **сделай деплой** — деплой текущего домена`
    },
    handleLocalCommand(text) {
      const result = parseCommand(text)
      if (!result.handled) return false

      if (result.localReply === 'help') {
        this.pushMessage({ sender: 'bot', text: this.getHelpText(), format: 'markdown' })
        this.scrollToBottom()
        return true
      }
      if (result.localReply === 'clear') {
        this.messages = [{
          sender: 'bot', text: 'Контекст очищен.', author: AUTHOR_CUBUS, at: new Date(),
        }]
        this.contextObject = null
        this.contextEntity = null
        this.scrollToBottom()
        this.sendSilent('/clear')
        return true
      }
      if (result.localReply) {
        this.pushMessage({ sender: 'bot', text: result.localReply })
        this.scrollToBottom()
        return true
      }
      if (result.serverText) {
        this.sendToServer(result.serverText, true)
      }
      return true
    },
    sendMessage() {
      const text = this.formatCommand(this.inputText.trim())
      if (!text) return

      const resumeVoice = this.wasVoiceInput
      this.commandHistory.unshift(text)
      this.historyIndex = -1
      this.historyBrowsing = false
      this.pushMessage({ sender: 'user', text })
      this.inputText = ''
      this.wasVoiceInput = false
      this.scrollToBottom()

      if (this.handleLocalCommand(text)) {
        if (resumeVoice && this.isSpeechSupported) {
          this.$nextTick(() => { this.toggleVoiceInput() })
        }
        return
      }

      this.sendToServer(text)

      if (resumeVoice && this.isSpeechSupported) {
        this.$nextTick(() => {
          this.toggleVoiceInput()
        })
      }
    },
    sendSilent(text) {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
      this.ws.send(JSON.stringify({
        type: 'message',
        to: 'chatbot',
        text,
        silent: true,
        token: this.getToken(),
      }))
    },
    sendToServer(text, isCommand = false) {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.pushMessage({
          sender: 'bot',
          text: 'Нет подключения к серверу. Попробуйте позже.',
        })
        this.scrollToBottom()
        return
      }

      this.startWaiting()
      this.scrollToBottom()
      // PROP-401: авто-открытие взведено ПО УМОЛЧАНИЮ; гасит его только явный запрет в запросе
      // («не открывай», «без открытия»). Сам переход случится лишь если в ответе окажется ровно
      // одна внутренняя ссылка.
      this.autoOpenArmed = !saysDoNotOpen(text)
      this.ws.send(JSON.stringify({
        type: 'message',
        to: 'chatbot',
        text,
        aiMode: this.aiMode,
        useAi: this.aiMode !== 'off',
        mode: this.chat_mode,
        // PROP-388: признак ставится ТОЛЬКО на наш собственный перевод слэш-команды в фразу,
        // понятную серверу (`/obj Заказы` → «открой Заказы»). Текст человека уходит без него —
        // и разбирается как разговор, а не как команда по совпадению с шаблоном.
        command: isCommand,
        token: this.getToken(),
      }))
    },
    /**
     * Поставить «собеседник думает» и завести предохранитель.
     *
     * Точки теперь живут до НАСТОЯЩЕГО ответа, а у агента он приходит через минуты — значит
     * молчание из-за сбоя (задание упало, раннер не достучался) выглядело бы как бесконечное
     * раздумье. Через десять минут индикатор снимается сам: лучше честное «ответа нет», чем
     * вечная анимация, обещающая ответ.
     */
    startWaiting() {
      this.isWaiting = true
      this.waitingFor = ''
      clearTimeout(this.waitingTimer)
      this.waitingTimer = setTimeout(() => { this.isWaiting = false; this.waitingFor = '' }, 10 * 60 * 1000)
    },
    /**
     * Служебная ли это строка. Основной признак — `kind` от бэкенда (PROP-353); запасной —
     * ведущий 🔧 в тексте: в истории уже лежат строки, отправленные до этой правки, и после
     * перезагрузки они должны выглядеть так же, а не всплыть пузырями.
     */
    // Служебная строка — всё, что не реплика: показания приборов Debug (🔧), вехи хода
    // работы агента (⚙️) и передача управления между собеседниками («передал агенту»,
    // `kind==='pending'`). PROP-366: вехи рисовались пузырём с аватаром и подписью, и десяток
    // «выполняет команду» подряд выглядел разговором, которого не было. Признак вехи —
    // `transient` (она не ложится в историю), эмодзи оставлено запасным: по нему такие строки
    // узнают вкладки, открытые до этого билда. PROP-397: в режиме «Чат» служебные строки
    // скрываются целиком (см. `visibleMessages`), в «Отладке» — видны.
    isService(msg) {
      return msg.kind === 'service' || msg.kind === 'pending' || msg.transient === true
        || /^\s*(_\s*)?(🔧|⚙️)/.test(msg.text || '')
    },
    pushMessage(msg) {
      this.messages.push({
        ...msg,
        author: msg.author || (msg.sender === 'user' ? this.userName : AUTHOR_CUBUS),
        at: msg.at ? new Date(msg.at) : new Date(),
      })
    },
    formatTime(at) {
      const d = at instanceof Date ? at : new Date(at)
      if (!at || Number.isNaN(d.getTime())) return ''
      const pad = n => String(n).padStart(2, '0')
      // Секунды нужны не для красоты: в чате рядом стоят сообщения Кубуса, Ассистента и агента,
      // и без них соседние строки выглядят одномоментными — порядок и задержки не прочитать.
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    copyMessage(index) {
      const msg = this.messages[index]
      if (!msg) return
      navigator.clipboard.writeText(msg.text).then(() => {
        this.$set(this.messages, index, { ...msg, copied: true })
        setTimeout(() => {
          this.$set(this.messages, index, { ...msg, copied: false })
        }, 1500)
      })
    },
    renderMarkdown(text) {
      // PROP-386: разметка чистится и внутренние ссылки помечаются в одном месте на оба окна —
      // см. `services/chatMarkdown.js`.
      return renderChatMarkdown(text)
    },
    /**
     * Клик по ссылке внутрь платформы уводит РОУТЕРОМ, а не перезагрузкой страницы.
     *
     * Обработчик один на всю ленту: сообщения приходят потоком, и вешать его на разметку из
     * `v-html` пришлось бы после каждого ответа.
     */
    // PROP-400: тот же переход, что и по клику, но инициированный автоматически. Переход на
    // текущий адрес роутер отвергает исключением — молча гасим (человек уже на месте).
    openInternalHref(href) {
      if (this.$router.currentRoute.fullPath === href) return
      this.$router.push(href).catch(() => { /* дубль текущего адреса или отказ гарда */ })
    },
    onChatClick(e) {
      handleInternalClick(e, this.$router)
    },
    // --- подсказка команд (PROP-384) ------------------------------------------
    /**
     * Подставить команду в поле.
     *
     * Команда с аргументом НЕ отправляется сразу — в поле остаётся `/obj ` с пробелом, и
     * человек дописывает своё. Отправить `/obj` без имени значило бы получить в ответ
     * «укажите имя объекта», то есть шаг назад вместо шага вперёд.
     */
    pickCommand(c) {
      this.inputText = c.args ? `${c.cmd} ` : c.cmd
      // Гасим подсказку в следующем такте, а не сразу: наблюдатель за `inputText` отработает
      // ПОСЛЕ присваивания и вернул бы её обратно — список предлагал бы уже набранное.
      this.$nextTick(() => {
        this.commandsDismissed = true
        this.focusInput()
      })
    },

    /**
     * Стрелки: пока открыта подсказка — ходим по ней, иначе листаем историю ввода.
     *
     * Два назначения у одной клавиши — обычно плохо, но здесь состояния взаимоисключающие и
     * видимые: список либо на экране, либо нет. Спорить им не на чем.
     */
    onArrowUp(e) {
      if (this.commandHints.length) {
        e.preventDefault()
        this.commandIndex = (this.commandIndex - 1 + this.commandHints.length) % this.commandHints.length
        return
      }
      // PROP-418: в резиновом поле стрелка прежде всего двигает каретку между строками.
      // История поднимается только С ПЕРВОЙ строки — иначе многострочный текст стало бы
      // нечем править: первое же нажатие затирало бы набранное прошлой командой.
      if (!atFirstLine(e)) return
      this.historyUp(e)
    },
    onArrowDown(e) {
      if (this.commandHints.length) {
        e.preventDefault()
        this.commandIndex = (this.commandIndex + 1) % this.commandHints.length
        return
      }
      if (!atLastLine(e)) return
      this.historyDown(e)
    },
    onTab(e) {
      if (!this.commandHints.length) return
      e.preventDefault()
      this.pickCommand(this.commandHints[this.commandIndex])
    },
    // PROP-424: выбор агента разблокирован, но локального ещё нет. Говорим об этом прямо:
    // молча принять выбор и продолжать отвечать другим агентом значило бы соврать
    // переключателем — человек списал бы чужие ответы на выбранного агента.
    /**
     * PROP-468: текущий режим работы агента. Отсутствие поля в ответе — не ошибка: домен
     * раскатан до `PROP-464`, режим там не применяется, и переключатель показывать не за что.
     */
    async loadAgentMode() {
      try {
        this.agentMode = await loadAgentMode()
      } catch (e) {
        this.agentMode = null
      }
    },

    /**
     * Переключение режима. ⚠️ Настройка ОБЩАЯ для всех ИИ этого человека в этом домене — и для
     * окна чата, и для локального Клода, и для Cursor. Поэтому о смене говорим в переписке, а не
     * молча: человек должен понимать, почему агент вдруг стал (или перестал) отказывать.
     */
    async onAgentModeChange(value) {
      const previous = this.agentMode
      try {
        this.agentMode = await saveAgentMode(value)
        // ⚠️ ОБЫЧНОЕ сообщение, НЕ служебное. Найдено браузерной приёмкой: `kind: 'service'`
        // прячет строку в режиме «Чат» (PROP-397 показывает служебные только в «Отладке»), и
        // человек, переключивший режим, не видел об этом ни слова — ровно та же грань, что в
        // PROP-457 со сводкой источников. Смена режима касается всех его ИИ, о ней надо сказать.
        this.pushMessage({
          sender: 'bot',
          author: AUTHOR_CUBUS,
          text: `_Режим агента: **${this.agentModeLabel}**. ${modeHint(this.agentMode)} `
            + 'Настройка действует на все ИИ, работающие в этом домене под вашей учётной записью._',
        })
      } catch (e) {
        // Вернуть выбор обратно обязательно: иначе список показывает одно, а сервер знает другое.
        this.agentMode = previous
        this.pushMessage({
          sender: 'bot',
          author: AUTHOR_CUBUS,
          text: `_Режим агента не переключился: ${e && e.message ? e.message : 'сервер не ответил'}._`,
        })
      }
    },

    onAgentKindChange(value) {
      if (value !== 'local') return
      this.pushMessage({
        sender: 'bot',
        kind: 'service',
        author: AUTHOR_CUBUS,
        text: '_Локальный Клод ещё не подключён — отвечает демонстрационный агент._',
      })
    },
    // PROP-418: поле стало многострочным, и форму Enter больше не отправляет сам — отправляем
    // руками, оставляя Shift+Enter переносу строки. `isComposing` — набор через IME ещё идёт.
    onEnter(e) {
      if (e.isComposing) return
      e.preventDefault()
      if (!this.inputText.trim()) return
      this.onSubmit()
    },

    /**
     * Enter при открытой подсказке ВЫБИРАЕТ команду, а не отправляет набранное.
     *
     * Иначе «/» с открытым списком улетал бы в чат как сообщение — человек видит выбранную
     * строку и ждёт от Enter именно её.
     */
    onSubmit() {
      const chosen = this.commandHints[this.commandIndex]
      if (chosen) {
        const typed = this.inputText.trim()
        const rest = typed.toLowerCase().startsWith(chosen.cmd)
          ? typed.slice(chosen.cmd.length).trim()
          : null
        // «Готова к отправке» — команда набрана целиком и с аргументом, если он нужен.
        // Всё остальное Enter ДОПОЛНЯЕТ: человек видит выбранную строку и ждёт именно её.
        const complete = rest !== null && (!chosen.args || rest.length > 0)
        if (!complete) { this.pickCommand(chosen); return }
      }
      this.sendMessage()
    },

    // ⚠️ PROP-426: история поднимается ТОЛЬКО из пустого поля либо когда её уже листают.
    // Набранный многострочный запрос стрелка «вверх» заменяла прошлой командой, а «вниз»
    // очищала поле совсем. Проверки каретки (PROP-418) от этого не спасали: они решают, ГДЕ
    // курсор, а не МОЖНО ЛИ терять набранное — а в начале строки он стоит совершенно законно.
    historyUp(e) {
      if (this.commandHistory.length === 0) return
      if (!this.historyBrowsing && this.inputText.trim() !== '') return
      if (this.historyIndex < this.commandHistory.length - 1) {
        e.preventDefault()
        this.historyIndex += 1
        this.setFromHistory(this.commandHistory[this.historyIndex])
      }
    },
    historyDown(e) {
      if (!this.historyBrowsing || this.historyIndex < 0) return
      e.preventDefault()
      if (this.historyIndex > 0) {
        this.historyIndex -= 1
        this.setFromHistory(this.commandHistory[this.historyIndex])
      } else {
        this.historyIndex = -1
        this.setFromHistory('')
        this.historyBrowsing = false
      }
    },
    /** Подстановка из истории: не считается правкой человека, иначе листание сбрасывало бы себя. */
    setFromHistory(value) {
      this.historyBrowsing = true
      this.fromHistory = true
      this.inputText = value
      this.$nextTick(() => { this.fromHistory = false })
    },
    focusInput() {
      const input = this.$refs.chatInput
      if (input) {
        input.focus()
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatLog
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.chatbot-page {
  height: calc(100vh - 160px);
  display: flex;
  // Точка отсчёта для режима «поверх»: панель абсолютная, и без этого она отсчитывалась бы
  // от окна, а не от области чата — на странице с меню это заметно уезжает.
  position: relative;
  gap: 12px;
}

/* --- правая панель (PROP-382) --------------------------------------------- */
.chatbot-sidebar {
  display: flex;
  flex-direction: column;
  background: var(--white, #fff);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  position: relative;
  // Ширину задаёт инлайновый стиль (её тянут мышью), поэтому здесь только запрет на сжатие:
  // без него flex ужимает панель обратно, и ползунок «не работает».
  flex: none;
}

// «Рядом» — панель в потоке, переписка сужается. «Поверх» — панель накрывает правый край
// переписки и не отнимает у неё ширины; тень отделяет её от текста под ней.
.chatbot-sidebar--overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
}

.chatbot-sidebar-resizer {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  background: transparent;
  transition: background 0.15s;

  &:hover,
  &--active {
    background: rgba(115, 103, 240, 0.35);
  }
}

.chatbot-sidebar-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 12px 16px;
  border-bottom: 1px solid #ebe9f1;
}

.chatbot-sidebar-title {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chatbot-sidebar-actions {
  display: flex;
  gap: 4px;
  margin-left: auto;
}

.chatbot-sidebar-mode.btn {
  padding: 4px 6px;
  line-height: 1;
}

.chatbot-sidebar-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chatbot-sidebar-empty {
  font-size: 12px;
  color: #b9b9c3;
  line-height: 1.5;
}

.chatbot-sidebar-step {
  display: flex;
  gap: 8px;
  font-size: 11px;
  line-height: 1.45;
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  color: #6e6b7b;
  word-break: break-word;
}

.chatbot-sidebar-step-time {
  color: #b9b9c3;
  white-space: nowrap;
}

.chatbot-sidebar-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 12px 10px 16px;
  border-top: 1px solid #ebe9f1;
  font-size: 11px;
  color: #6e6b7b;
}

.chatbot-sidebar-participant {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.chatbot-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--white, #fff);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.chatbot-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #ebe9f1;
}

.chatbot-header-info h5 {
  font-size: 15px;
}

.chatbot-header-ai-toggle {
  display: flex;
  align-items: center;
}

.chatbot-mode-select {
  width: auto;
  min-width: 90px;
  font-size: 13px;
}

.chatbot-ai-select {
  width: auto;
  min-width: 120px;
  font-size: 13px;
}

.chatbot-agentmode-select {
  width: auto;
  /* PROP-468: «Работа с данными» — самая длинная подпись, иначе обрежется. */
  min-width: 170px;
  font-size: 13px;
}

.chatbot-agent-select {
  width: auto;
  /* PROP-419: подпись «Локальный Клод (в разработке)» длиннее прочих — иначе обрежется. */
  min-width: 210px;
  font-size: 13px;
}

.chatbot-participants {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  padding: 8px 20px;
  border-bottom: 1px solid #ebe9f1;
  background: #fafafc;
}

.chatbot-participant {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  // Участник в цепочке читается тёмным: кто отвечает, видно по лампе и по этому контрасту,
  // отдельной подписи для этого не нужно.
  color: #5e5873;
  cursor: default;

  &--off {
    opacity: 0.45;
  }

  &--next .chatbot-participant-name {
    font-weight: 600;
  }
}

.chatbot-participant-avatar {
  flex-shrink: 0;
  color: #fff;
  font-size: 9px;
}

// Участник вне выбранного режима гасится именно СЕРЫМ, а не полупрозрачным цветным: выцветшая
// заливка читалась бы как ещё один оттенок светофора рядом с ним.
.chatbot-participant--off .chatbot-participant-avatar {
  filter: grayscale(1);
}

.chatbot-light {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #b9b9c3;

  // Серый — «не участвует в выбранном режиме». Отдельный класс, а не умолчание: цвет должен
  // приходить с сервера явно, иначе потерянное поле выглядит как выключенный участник.
  &--grey {
    background: #c8c8d0;
  }

  &--green {
    background: #28c76f;
  }

  &--yellow {
    background: #ff9f43;
  }

  &--red {
    background: #ea5455;
  }
}

.chatbot-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chatbot-message-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;

  &--user {
    justify-content: flex-end;
  }

  // Служебные строки идут пачкой по десятку на сообщение: обычный межстрочный зазор в 12px
  // растянул бы отладку на два экрана и оторвал её от реплики, к которой она относится.
  &--service {
    margin: -8px 0;
  }
}

.chatbot-service {
  flex: 1;
  min-width: 0;
  padding: 0 4px 0 40px;   // отступ слева — под колонку аватаров, чтобы не спорить с репликами
  font-size: 11px;
  line-height: 1.45;
  color: #b9b9c3;
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  word-break: break-word;

  // ⚠️ ::v-deep обязателен: содержимое строки приходит через `v-html`, и на него НЕ вешается
  // атрибут scoped-стилей — правило `.chatbot-service p` компилируется в `p[data-v-…]` и до
  // этого `p` не достаёт. Замер: `display: block`, `margin-bottom: 14px`, время уезжало на
  // отдельную строку в 36 пикселях под текстом. Ровно та же ловушка съела и `margin: 0`,
  // и `em`, и `code` — они молчали с самого начала, потому что «не применилось» выглядит
  // в точности как «применилось, но ничего не изменило».
  ::v-deep p {
    display: inline;      // время и текст стоят одной строкой, а не столбиком
    margin: 0;
  }

  ::v-deep code {
    font-size: 11px;
    color: #a8aab7;
    background: transparent;
    padding: 0;
  }

  ::v-deep em {
    font-style: normal;   // курсив был признаком служебности, теперь им служит сам стиль
  }
}

// Время служебной строки: приглушено сильнее самой строки — это подпись к показанию, а не
// показание. Не переносится и не сжимается, чтобы отметка не разрывалась пополам.
.chatbot-service-time {
  margin-left: 4px;
  opacity: 0.65;
  white-space: nowrap;
}

.chatbot-avatar {
  flex-shrink: 0;
}

.chatbot-typing {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 12px 16px;
}

/* PROP-408: секундомер ожидания. Моноширинные цифры — чтобы строка не дёргалась каждую секунду. */
.chatbot-typing-elapsed {
  margin-left: 4px;
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
}

.chatbot-typing-label {
  font-size: 12px;
  color: #6e6b7b;
  margin-right: 2px;
}

.chatbot-typing-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #aaa;
  animation: typing-bounce 1.2s ease-in-out infinite;

  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.4s; }
}

@keyframes typing-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-5px); opacity: 1; }
}

.chatbot-bubble {
  max-width: 70%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.45;
  word-wrap: break-word;
  white-space: pre-wrap;
  position: relative;

  &:hover .chatbot-copy-btn {
    opacity: 1;
  }

  &--bot {
    background: #f3f2f5;
    color: #333;
    border-bottom-left-radius: 4px;
  }

  &--user {
    background: #7367f0;
    color: #fff;
    border-bottom-right-radius: 4px;
  }
}

.chatbot-meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 3px;
  padding-right: 20px; // место для кнопки копирования, всплывающей по наведению
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
}

.chatbot-author {
  font-weight: 600;
  opacity: 0.85;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chatbot-time {
  flex-shrink: 0;
  opacity: 0.6;
}

.chatbot-copy-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0;
  background: none;
  border: none;
  padding: 2px 4px;
  border-radius: 4px;
  cursor: pointer;
  color: inherit;
  transition: opacity 0.15s, background 0.15s;
  line-height: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
}

.chatbot-markdown ::v-deep {
  p:last-child {
    margin-bottom: 0;
  }

  pre {
    background: rgba(0, 0, 0, 0.05);
    padding: 8px 12px;
    border-radius: 6px;
    overflow-x: auto;
  }

  code {
    font-size: 13px;
  }

  ul, ol {
    padding-left: 20px;
    margin-bottom: 0.5em;
  }

  table {
    border-collapse: collapse;
    margin: 0.5em 0;

    th, td {
      border: 1px solid #ddd;
      padding: 4px 8px;
    }
  }
}

.chatbot-statusbar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 20px;
  border-top: 1px solid #ebe9f1;
  background: #f8f8f8;
  font-size: 13px;
  color: #6e6b7b;
}

.chatbot-statusbar-icon {
  flex-shrink: 0;
  color: #7367f0;
}

.chatbot-statusbar-label {
  font-weight: 500;
}

.chatbot-statusbar-link {
  color: #7367f0;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
}

.chatbot-statusbar-id {
  color: #b9b9c3;
  font-size: 12px;
}

.chatbot-statusbar-separator {
  color: #b9b9c3;
  flex-shrink: 0;
}

.chatbot-statusbar-entity {
  color: #28c76f;
  font-weight: 600;
}

.chatbot-input-area {
  padding: 16px 20px;
  border-top: 1px solid #ebe9f1;
  position: relative;
}

/* Подсказка команд (PROP-384): всплывает НАД полем ввода, а не под ним — поле стоит у нижнего
   края окна, и список под ним оказался бы за границей экрана. */
.chatbot-commands {
  position: absolute;
  left: 20px;
  right: 20px;
  bottom: calc(100% - 8px);
  max-height: 260px;
  overflow-y: auto;
  background: var(--white, #fff);
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  z-index: 10;
}

.chatbot-command {
  display: flex;
  gap: 12px;
  align-items: baseline;
  padding: 7px 12px;
  cursor: pointer;

  &--active {
    background: rgba(115, 103, 240, 0.1);
  }
}

.chatbot-command-name {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  color: #7367f0;
  white-space: nowrap;
}

.chatbot-command-hint {
  font-size: 12px;
  color: #6e6b7b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chatbot-form {
  display: flex;
  /* PROP-418: поле растёт вверх, кнопки остаются у нижнего края. */
  align-items: flex-end;
  gap: 8px;
}

.chatbot-textarea {
  line-height: 1.35;
  overflow-y: auto;
}

.chatbot-mic-recording {
  animation: mic-pulse 1s ease-in-out infinite;
}

@keyframes mic-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.dark-layout {
  .chatbot-container {
    background: #283046;
  }

  .chatbot-header,
  .chatbot-input-area,
  .chatbot-statusbar {
    border-color: #3b4253;
  }

  .chatbot-statusbar {
    background: #242b3d;
    color: #b4b7bd;
  }

  .chatbot-bubble--bot {
    background: #3b4253;
    color: #d0d2d6;
  }

  .chatbot-typing-label {
    color: #b4b7bd;
  }

  .chatbot-sidebar {
    background: #283046;
  }

  .chatbot-sidebar-header,
  .chatbot-sidebar-footer {
    border-color: #3b4253;
  }

  .chatbot-sidebar-step {
    color: #b4b7bd;
  }

  .chatbot-commands {
    background: #283046;
    border-color: #3b4253;
  }

  .chatbot-command-hint {
    color: #b4b7bd;
  }

  .chatbot-service {
    color: #676d7d;

    code {
      color: #7983a1;
    }
  }
}
</style>
